import { IconCheck, IconPlus, IconSearch } from '@tabler/icons-react';
import { Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { iconForGroup } from './bindPanel.utils.ts';
import {
	CatalogueScroller,
	DataPointIconTile,
	DataPointRow,
	EyebrowLabel,
	GroupLabel,
	GroupSection,
	RowText,
	SampleValuePill,
	SearchInput,
	SelectDataPointHeading,
	ToggleButton,
} from './bindPanel.styled.ts';
import type { GroupedDataPoints } from './BindPanel.types.ts';

interface Props {
	search: string;
	onSearchChange: (value: string) => void;
	groups: GroupedDataPoints[];
	selectedPaths: string[];
	onToggleDataPoint: (path: string) => void;
}

// The "SELECT A DATA POINT" section: heading + help copy, the search input,
// and the grouped, toggleable list of catalogue data points.
export const DataPointCatalogue = ({
	search,
	onSearchChange,
	groups,
	selectedPaths,
	onToggleDataPoint,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	return (
		<>
			<SelectDataPointHeading>
				<EyebrowLabel>
					{tMipManage('master_documents.field_binding.panel.select_heading')}
				</EyebrowLabel>
				<Text size="xs" c="dimmed">
					{tMipManage('master_documents.field_binding.panel.select_help')}
				</Text>
			</SelectDataPointHeading>

			<SearchInput
				placeholder={tMipManage('master_documents.field_binding.panel.search_placeholder')}
				radius="md"
				leftSection={<IconSearch size={14} />}
				value={search}
				onChange={(event) => onSearchChange(event.currentTarget.value)}
			/>

			<CatalogueScroller>
				{groups.map(({ group, dataPoints }) => {
					const GroupIcon = iconForGroup(group);
					return (
						<GroupSection key={group}>
							<GroupLabel>
								<GroupIcon size={14} />
								<Text size="xs" fw={600} c="dimmed">
									{group}
								</Text>
							</GroupLabel>
							{dataPoints.map((dataPoint) => {
								const isSelected = selectedPaths.includes(dataPoint.path);
								return (
									<DataPointRow key={dataPoint.path}>
										<DataPointIconTile>
											<GroupIcon size={16} />
										</DataPointIconTile>
										<RowText>
											<Text size="sm" fw={500}>
												{dataPoint.label}
											</Text>
											{dataPoint.optional && (
												<Text size="xs" c="dimmed">
													{tMipManage(
														'master_documents.field_binding.panel.optional_hint'
													)}
												</Text>
											)}
										</RowText>
										{dataPoint.sampleValue && (
											<SampleValuePill>
												<Text size="xs" c="dark.5">
													{dataPoint.sampleValue}
												</Text>
											</SampleValuePill>
										)}
										<ToggleButton
											$selected={isSelected}
											data-testid={`bind-panel-data-point-${dataPoint.path}`}
											aria-label={
												isSelected
													? tMipManage(
															'master_documents.field_binding.panel.remove_aria',
															{ label: dataPoint.label }
														)
													: tMipManage(
															'master_documents.field_binding.panel.add_aria',
															{ label: dataPoint.label }
														)
											}
											onClick={() => onToggleDataPoint(dataPoint.path)}
										>
											{isSelected ? (
												<IconCheck size={14} />
											) : (
												<IconPlus size={14} />
											)}
										</ToggleButton>
									</DataPointRow>
								);
							})}
						</GroupSection>
					);
				})}
				{groups.length === 0 && (
					<Text size="sm" c="dimmed">
						{tMipManage('master_documents.field_binding.panel.no_matches')}
					</Text>
				)}
			</CatalogueScroller>
		</>
	);
};
